import { inject, Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { CheckPinResult } from '../models';
import { ApiService, ConverterService, CryptoProvider, LogService } from '../providers/index';
import { CredentialSalts } from '../types';

@Injectable({
  providedIn: 'root',
})
export class AccountDataService {
  protected readonly api = inject(ApiService);
  protected readonly cryptoProvider = inject(CryptoProvider);
  protected readonly converter = inject(ConverterService);

  public async checkIfPinIsValid(pin: string): Promise<CheckPinResult> {
    if (!pin) {
      LogService.error('No PIN provided to check');
      return {
        success: false,
        message: _('server.error.validatepin.missing_pin'),
      };
    }

    const salts = await this.getSalts();

    if (!salts.pinSalt) {
      LogService.error('No PIN salt available');
      return {
        success: false,
        message: _('server.error.validatepin.missing_salt'),
      };
    }

    const pinHash = await this.cryptoProvider.computeHash(pin, salts.pinSalt);
    return firstValueFrom(
      this.api.post('user/validatepin', {
        pinHash: this.converter.uIntArrayToBase64(pinHash),
      }),
    );
  }

  public getSalts(): Promise<CredentialSalts> {
    return firstValueFrom(
      this.api.restGet<{ passwordSalt: string; pinSalt: string }>('user/salts').pipe(
        map((salts) => ({
          passwordSalt: this.converter.hexStringToUint8Array(salts.passwordSalt),
          pinSalt: salts.pinSalt ? this.converter.hexStringToUint8Array(salts.pinSalt) : null,
        })),
      ),
    );
  }
}
